import classNames from 'classnames'
import { useRouter } from 'next/router'
import { ReactElement, useEffect } from 'react'
import styles from './Registration.module.scss'
import { Billing, Confirmation, MeterDetails, Overview, PersonalData, DeliveryAddress } from './steps'
import { RegistrationProps, RegistrationSteps } from './types'
import RegistrationSideCard from 'features/registration/components/registration-side-card/RegistrationSideCard'
import { CustomerFlows, InputGroupKeys, MeterDetailsFieldKeys, PersonalDataFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { isReserved } from 'utils/producers'
import { routes } from 'utils/routes'
import { determineSimulationUsage, requestAndSavePropositions } from 'features/simulation/utils'
import type { StepProps } from './steps/types'
import RegistrationLoading from 'features/registration-loading/RegistrationLoading'
import { CommonGTMEvents, CommonTrackingParams } from 'types/tracking'
import { isProducerOverCapacity } from 'features/usage-form/utils'
import { goToStep, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { SessionStorageKeys } from 'constants/constants'
import PageFlag from 'components/page-flag/PageFlag'
import { resetDataLayer, trackEvent, trackGTMEvent } from 'utils/tracking'
import { getSessionStorageItem } from 'utils/sessionStorage'
import FlowHeader from 'components/flow-header/FlowHeader'
import { SIMULATION_TYPES } from '@boltenergy-be/shared'
import { useGetProductsQuery } from 'store/api/bolt-api'
import { INTERNAL_REGISTRATION_URL_PARAMS } from 'constants/customer-flow'

const Registration = ({ producer, overrideNextStep, hideHeader, activeAction }: RegistrationProps) => {
  // Redux store
  const { inputs, flows, propositions } = useStoreSelector((store) => store.customer)
  const { opportunityId } = useStoreSelector((store) => store.app)
  const { confirmationFromPortalRegistration, simulationType, campaignName } = useStoreSelector((store) => store.marketing)
  const { didSimulation } = flows.simulation
  const dispatch = useStoreDispatch()

  // Redux query
  // Fetch these at the start of the registration flow to prevent registration submit after midnight with other pricelists
  // Especially important when going into a new month (23.30 last of month, start registration, submit after midnight, first of next month)
  const { data: products } = useGetProductsQuery()

  // Next Router
  const router = useRouter()
  const { locale, query } = router
  const isNotToBeConfirmed = query[INTERNAL_REGISTRATION_URL_PARAMS.IS_NOT_TO_BE_CONFIRMED] === 'true'
  const isClosedWon = query[INTERNAL_REGISTRATION_URL_PARAMS.IS_CLOSED_WON] === 'true'
  const isInvalidFlow =
    query[INTERNAL_REGISTRATION_URL_PARAMS.IS_INVALID_NO_EAN_FLOW] === 'true' ||
    query[INTERNAL_REGISTRATION_URL_PARAMS.IS_INVALID_CREDIT_CHECK_FLOW] === 'true'

  // Constants
  const { currentStep } = flows[CustomerFlows.REGISTRATION]
  const cantConfirm = isClosedWon || isNotToBeConfirmed || isInvalidFlow
  const isExpressRegistration = inputs.simulation.chosenSimulationType === SIMULATION_TYPES.express
  const isPricingSimulationRegistration = inputs.simulation.chosenSimulationType === SIMULATION_TYPES.pricingSimulation
  const producerOverCapacity = isProducerOverCapacity(producer, inputs.simulation)
  const reserved = isReserved(producer)

  /**
   * Does some checks & redirects if needed
   */
  useEffect(() => {
    // Express & no propositions, redirect to loading page
    if (isExpressRegistration && !propositions?.simulatedPropositions && !opportunityId) {
      router.push(routes(locale).registrationLoading)
      return
    }

    if (
      // (Pricing Simulation && no propositions && no simulation done && no opportunityId) ||
      (isPricingSimulationRegistration && !propositions?.simulatedPropositions && !didSimulation && !opportunityId) ||
      // (Not confirmation from portal && not sold out or reserved)
      (!confirmationFromPortalRegistration && reserved)
    ) {
      router.push(routes(locale, producer.parentSlug).producerInfo)
      return
    }
  }, [])

  /**
   * Triggered on first render
   * Reset dataLayer state to clear out data from simulation, and track 'Subscription Started' event @Semetis
   */
  useEffect(() => {
    resetDataLayer()
    trackGTMEvent('subscription_started', {
      bolt_product: propositions.selectedProposition,
      with_promo_code: !!inputs.simulation[SimulationFieldKeys.PROMO_CODE]?.code
    })

    const fromPage = getSessionStorageItem(SessionStorageKeys.FROM) || undefined
    trackEvent(
      { category: 'subscription', action: 'start' },
      {
        [CommonTrackingParams.SELECTED_PRODUCT]: propositions.selectedProposition,
        [CommonTrackingParams.SIMULATION_TYPE]: simulationType,
        [CommonTrackingParams.PORTAL_CONFIRMATION]: confirmationFromPortalRegistration,
        [CommonTrackingParams.CAMPAIGN]: campaignName,
        ...(fromPage && { [CommonTrackingParams.FROM_PAGE]: fromPage })
      }
    )
  }, [])

  /**
   * Scroll to top after navigating between steps
   * Track step opened event
   */
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentStep])

  /**
   * Goes to the next step
   */
  const handleNextStep = (step: RegistrationSteps) => {
    if (producerOverCapacity) return
    dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step }))
  }

  /**
   * Handles the banner on click event
   * Shows simulation or redirects to producer page if simulation has been done
   */
  const handleOnBannerClick = () => {
    if (didSimulation) {
      return router.push(routes(locale).producers)
    } else {
      // Track 'Simulation Started' event @Semetis
      trackGTMEvent(CommonGTMEvents.SIMULATION_STARTED)

      return router.push(routes(locale).simulation)
    }
  }

  /**
   * Resimulate the price with the given parameters
   *
   * @param {InputGroupKeys} group
   * @param {PersonalDataFieldKeys | MeterDetailsFieldKeys} key
   * @param {string | boolean} value
   * @param {boolean} [skipSimulation]
   */
  const saveAndResimulate = (
    group: InputGroupKeys,
    key: PersonalDataFieldKeys | MeterDetailsFieldKeys,
    value: string | boolean,
    skipSimulation?: boolean
  ) => {
    // Update the needed inputs
    dispatch(updateInput({ group, key, value }))
    dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.USAGE, value: determineSimulationUsage() }))

    // Request new propositions
    if (!skipSimulation) requestAndSavePropositions()
  }

  /**
   * Returns the correct content view for the current step
   *
   * @returns {ReactElement}
   */
  const getContentForCurrentStep = (): ReactElement<any> => {
    const stepProps: Omit<StepProps, 'nextStep'> = { producer, producerOverCapacity, saveAndResimulate }

    switch (currentStep) {
      case RegistrationSteps.DELIVERY_ADDRESS:
        return <DeliveryAddress nextStep={() => handleNextStep(RegistrationSteps.PERSONAL_DATA)} {...stepProps} />

      case RegistrationSteps.PERSONAL_DATA:
        return <PersonalData nextStep={() => handleNextStep(RegistrationSteps.METER_DETAILS)} {...stepProps} />

      case RegistrationSteps.METER_DETAILS:
        return <MeterDetails nextStep={() => handleNextStep(RegistrationSteps.BILLING)} {...stepProps} />

      case RegistrationSteps.BILLING:
        return <Billing nextStep={() => handleNextStep(RegistrationSteps.OVERVIEW)} {...stepProps} />

      case RegistrationSteps.OVERVIEW:
        return <Overview {...stepProps} {...{ overrideNextStep, products }} />

      case RegistrationSteps.CONFIRMATION:
        return <Confirmation {...stepProps} {...{ activeAction }} />
    }
  }

  return isExpressRegistration && !propositions?.simulatedPropositions ? (
    <RegistrationLoading hideHeader={hideHeader} />
  ) : (
    <>
      <PageFlag onClick={handleOnBannerClick} />

      {!confirmationFromPortalRegistration && !hideHeader && <FlowHeader flow={CustomerFlows.REGISTRATION} />}

      <main className={classNames(styles.registration)}>
        <div className={classNames('container', styles.wrapper)}>
          <div
            className={classNames(styles.content, {
              [styles.confirmation]: currentStep === RegistrationSteps.CONFIRMATION
            })}
          >
            {getContentForCurrentStep()}
          </div>

          {currentStep !== RegistrationSteps.CONFIRMATION && !cantConfirm && <RegistrationSideCard {...{ producer }} />}
        </div>
      </main>
    </>
  )
}

export default Registration
